// The payment step of a recurring booking from a postpaidcreditcard campaign: nothing is paid now; each occurrence
// still unpaid 30 min after it ends is charged from the stored card chosen here (falcon recurring-billing.js), after
// the saldo where the venue takes that first. Asks for a card and the fixed consent (RECURRING_CARD_CONSENT_VERSION).
import React, { useEffect, useState } from 'react'
import { Button, FormGroup, Input, Label, Alert, Spinner } from 'reactstrap'
import Checkbox from '@material-ui/core/Checkbox'
import { useTranslation } from 'react-i18next'
import { cardList, defaultCardId } from '../utils/storedcards'
import RecurringCardConsentNote from './RecurringCardConsentNote'

// Bump whenever the texts behind 'recurring_card_consent' change; falcon keeps the list it accepts.
export const RECURRING_CARD_CONSENT_VERSION = 'recurring-postpaidcard-1'

// The season's last day as 'YYYY-MM-DD' or similar; a card valid through its expiry month.
const expiresBefore = (card, seasonend) => {
  if (!card || !seasonend || !card.expires) return false
  const [mm, yy] = card.expires.split('/')
  const lastValid = new Date(2000 + Number(yy), Number(mm), 0)
  return new Date(seasonend) > lastValid
}

function RecurringCardCheckout({ cards, seasonend, saldoFirst, onAddCard, onConfirm, disabled, addingCard, addCardFailed }) {
  const { t } = useTranslation()
  const list = cardList(cards)
  const [cardid, setCardid] = useState(defaultCardId(list))
  const [consent, setConsent] = useState(false)
  // the separate, optional consent for a card added here (RecurringCardConsentNote)
  const [venueConsent, setVenueConsent] = useState(false)

  // Cards arrive from Firestore after the first render: preselect once they do.
  const firstValid = defaultCardId(list)
  useEffect(() => {
    if (!cardid && firstValid) setCardid(firstValid)
  }, [cardid, firstValid])

  if (list.length === 0) {
    return (
      <div style={{ marginTop: 10 }}>
        <p>{t('recurring_card_needs_card')}</p>
        <Button color='primary' disabled={addingCard} onClick={() => onAddCard({ recurringConsent: venueConsent })}>
          {addingCard ? <Spinner size='sm' /> : t('add_card')}
        </Button>
        <div style={{ fontSize: 'small', marginTop: 5 }}>{t('card_storage_note')}</div>
        <RecurringCardConsentNote checked={venueConsent} onChange={setVenueConsent} />
        {addCardFailed ? <div style={{ color: '#b00020', marginTop: 5 }}>{t('card_add_failed_init')}</div> : null}
      </div>
    )
  }

  const chosen = list.find((c) => c.id === cardid)
  const canConfirm = !disabled && consent && chosen && !chosen.expired

  return (
    <div style={{ marginTop: 10 }}>
      <p>{t(saldoFirst ? 'recurring_card_how_saldo' : 'recurring_card_how')}</p>
      <div style={{ marginTop: 10, marginBottom: 5 }}>
        <strong>{t('subscription_choose_card')}</strong>
      </div>
      {list.map((c) => (
        <FormGroup check key={c.id}>
          <Label check style={c.expired ? { color: '#999' } : null}>
            <Input type='radio' name='recurringcard' checked={cardid === c.id} disabled={c.expired} onChange={() => setCardid(c.id)} />{' '}
            <img src={process.env.PUBLIC_URL + c.icon} alt={c.type} style={{ height: 16, verticalAlign: 'middle', marginRight: 6 }} />
            {c.brandShown ? '' : c.type + ' '}{c.label} {c.expires}
            {c.expired ? ' — ' + t('card_expired_label') : ''}
          </Label>
        </FormGroup>
      ))}
      <Button size='sm' color='link' style={{ paddingLeft: 0 }} disabled={addingCard} onClick={() => onAddCard({ recurringConsent: venueConsent })}>
        {addingCard ? <Spinner size='sm' /> : t('add_card')}
      </Button>
      <RecurringCardConsentNote checked={venueConsent} onChange={setVenueConsent} />
      {addCardFailed ? <div style={{ color: '#b00020' }}>{t('card_add_failed_init')}</div> : null}
      {chosen && !chosen.expired && expiresBefore(chosen, seasonend) ? (
        <Alert color='warning' style={{ marginTop: 10 }}>{t('recurring_card_expires_before_end')}</Alert>
      ) : null}

      <Alert color='light' style={{ marginTop: 10, display: 'flex', alignItems: 'flex-start' }}>
        <Checkbox checked={consent} onChange={(e) => setConsent(e.target.checked)} color='primary' style={{ paddingTop: 0 }} />
        <span>{t('recurring_card_consent')}</span>
      </Alert>

      <Button color='primary' block disabled={!canConfirm} onClick={() => onConfirm({ cardid })}>
        {t('recurring_card_book')}
      </Button>
    </div>
  )
}

export default RecurringCardCheckout
